/**
 * Thin strip across the top of /login + /signup.
 *
 * Live SPX telemetry now sits behind auth (GV-02: regime/king data is
 * paywalled), so this unauthenticated strip no longer polls
 * /api/regime/SPX or /api/king/SPX — doing so would 401 in a retry loop
 * and show nothing. Instead it renders a STATIC sample read, clearly
 * labelled as a preview (the "SAMPLE" tag + a "SIGN IN FOR LIVE" status
 * chip) so the visual chrome stays intact without implying these numbers
 * are live. The real, streaming version lives on the authenticated
 * /app/* pages (AmbientTicker).
 */

import React from 'react';
import { formatStrike } from '../../lib/format';

// Representative, plausible-but-static numbers. Intentionally NOT fetched
// — they illustrate the layout a signed-in user gets without making a
// false "live" claim. Kept in the same shape the live read used so the
// markup below is unchanged.
const SAMPLE = {
  regimeLabel: 'POSITIVE_GAMMA',
  kingStrike: 5850,
  kingDistancePct: 0.0042,
  flipPoint: 5832,
} as const;

const AmbientStrip: React.FC = () => {
  const kingStrike = `$${formatStrike(SAMPLE.kingStrike)}`;
  const kingDist = ` (+${(SAMPLE.kingDistancePct * 100).toFixed(2)}%)`;
  const flip = `$${formatStrike(SAMPLE.flipPoint)}`;

  return (
    <div className="login-strip" aria-label="Sample SPX telemetry — sign in for live data">
      <span className="login-tick">
        {/* Neutral (non-pulsing) dot — this is a static sample, not a live feed. */}
        <span className="dot" style={{ background: 'var(--text-3)', animation: 'none' }} aria-hidden="true" />
        SPX REGIME&nbsp;<strong>{SAMPLE.regimeLabel}</strong>
      </span>
      <span className="login-tick">KING STRIKE&nbsp;<strong>{kingStrike}{kingDist}</strong></span>
      <span className="login-tick">GEX FLIP&nbsp;<strong>{flip}</strong></span>
      {/* Honest framing: these are illustrative figures, and live data is
          behind sign-in. */}
      <span className="login-tick">SAMPLE&nbsp;<strong>SIGN IN FOR LIVE</strong></span>
    </div>
  );
};

export default AmbientStrip;
